'use client'

import { useEffect, useRef, useState } from 'react'

import styled from 'styled-components'

import type { ProductListItem } from '../../shared/api/generated'
import { colors } from '../../shared/constants/colors'
import { ProductCardsList } from '../../shared/ui-kit/actual/ProductCardsList'
import type { ProductListCardVariant } from '../../shared/ui-kit/actual/ProductListCard'
import { ProductListHeader } from '../../shared/ui-kit/actual/ProductListHeader'
import { loadMoreProducts } from './loadMoreProducts'

interface ProductsViewSectionProps {
  initialProducts: ProductListItem[]
  totalCount?: number
}

const PRODUCTS_PAGE_SIZE = 8

export function ProductsViewSection({ initialProducts, totalCount }: ProductsViewSectionProps) {
  const [variant, setVariant] = useState<ProductListCardVariant>('card')
  const [products, setProducts] = useState(initialProducts)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [isExhausted, setIsExhausted] = useState(false)
  const loadMoreMarkerRef = useRef<HTMLDivElement>(null)
  const hasMore =
    !isExhausted &&
    (typeof totalCount === 'number'
      ? products.length < totalCount
      : products.length > 0 && products.length % PRODUCTS_PAGE_SIZE === 0)

  useEffect(() => {
    const marker = loadMoreMarkerRef.current

    if (!marker || !hasMore || isLoadingMore || loadError) return

    const observer = new IntersectionObserver(
      entries => {
        if (!entries[0]?.isIntersecting) return

        observer.disconnect()
        setIsLoadingMore(true)

        loadMoreProducts(products.length, PRODUCTS_PAGE_SIZE)
          .then(response => {
            const loadedProducts = (response.items || []).filter(
              product => typeof product.productId === 'number' && product.productId > 0
            )
            const currentIds = new Set(products.map(product => product.productId))
            const uniqueProducts = loadedProducts.filter(
              product => !currentIds.has(product.productId)
            )
            const responseTotalCount = response.totalCount ?? totalCount

            setProducts(currentProducts => [...currentProducts, ...uniqueProducts])
            setIsExhausted(
              loadedProducts.length < PRODUCTS_PAGE_SIZE ||
                uniqueProducts.length === 0 ||
                (typeof responseTotalCount === 'number' &&
                  products.length + uniqueProducts.length >= responseTotalCount)
            )
            setLoadError(false)
          })
          .catch(() => setLoadError(true))
          .finally(() => setIsLoadingMore(false))
      },
      { rootMargin: '300px 0px' }
    )

    observer.observe(marker)

    return () => observer.disconnect()
  }, [hasMore, isLoadingMore, loadError, products, totalCount])

  if (products.length === 0) return null

  return (
    <Section>
      <ProductListHeader onVariantChange={setVariant} title={'Популярное'} variant={variant} />

      <ProductsViewport>
        <ProductCardsList products={products} variant={variant} />
      </ProductsViewport>
      {hasMore && <LoadMoreMarker ref={loadMoreMarkerRef} />}
      {isLoadingMore && <LoadingStatus>Загружаем товары…</LoadingStatus>}
      {loadError && (
        <RetryButton onClick={() => setLoadError(false)} type={'button'}>
          Повторить загрузку
        </RetryButton>
      )}
    </Section>
  )
}

const Section = styled.section`
  width: 100%;
  margin-top: 32px;
`

const ProductsViewport = styled.div`
  width: 100%;
  overflow-x: auto;
  padding-bottom: 8px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const LoadMoreMarker = styled.div`
  width: 100%;
  height: 1px;
`

const LoadingStatus = styled.p`
  margin: 12px 0 0;
  color: ${colors.grey};
  font-size: 14px;
  text-align: center;
`

const RetryButton = styled.button`
  display: block;
  margin: 12px auto 0;
  padding: 8px 20px;
  border: 0;
  border-radius: 12px;
  background: ${colors.primary};
  color: ${colors.white};
  font-size: 14px;
  cursor: pointer;
`
